/**
 * Search with combined filters (F2): text + mood + tag + date
 * range, all local-first and instant (LIKE nocase, see D2).
 */
import { createRoute, useSearch } from "@tanstack/solid-router";
import { useNavigate } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { Search as SearchIcon } from "@fleet/icons";
import { type EntryFilters as EntryFilterArgs, isMoodValue } from "@journal/shared";
import { useFilterEntriesQuery, useTagsQuery } from "@journal/shared/solid";
import { appRoute } from "./route";
import { PageContainer } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { SearchIllustration } from "@/components/ui/illustrations";
import { useI18n } from "@/providers/i18n-provider";
import { EntryCard } from "@/features/journal/components/entry-card";
import { EntryFilters, type EntryFilterState } from "@/features/journal/components/entry-filters";
import { useJournalData } from "@/features/journal/hooks/use-journal-data";

const EMPTY_FILTERS: EntryFilterState = {
  term: "",
  mood: null,
  tagId: null,
  startKey: "",
  endKey: "",
};

interface SearchParams {
  mood?: number;
  tagId?: string;
  term?: string;
  from?: string;
  to?: string;
}

function SearchPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { db, drizzle, userId, staleTime } = useJournalData();
  const search = useSearch({ from: "/_app/search" });

  // Deep-link / interconnection: derives the filters from the query params (e.g.
  // when coming from "mood in stats → entries of that mood" or "tag → entries",
  // or from a shared/reloaded link). The URL is the source of truth: the
  // state is rebuilt the same on reload (idempotent).
  const fromUrl = (): EntryFilterState => {
    const p = search();
    return {
      ...EMPTY_FILTERS,
      term: typeof p.term === "string" ? p.term : "",
      mood: isMoodValue(p.mood) ? p.mood : null,
      tagId: typeof p.tagId === "string" ? p.tagId : null,
      startKey: typeof p.from === "string" ? p.from : "",
      endKey: typeof p.to === "string" ? p.to : "",
    };
  };

  const [filters, setFilters] = createSignal<EntryFilterState>(fromUrl());

  // URL → signal: reflects external navigations (back/forward, pasted link,
  // navigating to /search with new params while already mounted).
  createEffect(() => {
    setFilters(fromUrl());
  });

  // signal → URL: each filter change writes idempotent params (omitting
  // empties for clean URLs). `replace` avoids bloating the history while typing.
  const syncUrl = (next: EntryFilterState) => {
    void navigate({
      to: "/search",
      search: () => {
        const out: SearchParams = {};
        if (next.term.trim() !== "") {
          out.term = next.term.trim();
        }
        if (next.mood !== null) {
          out.mood = next.mood;
        }
        if (next.tagId !== null) {
          out.tagId = next.tagId;
        }
        if (next.startKey !== "") {
          out.from = next.startKey;
        }
        if (next.endKey !== "") {
          out.to = next.endKey;
        }
        return out;
      },
      replace: true,
    });
  };

  const tags = useTagsQuery({ db, drizzle, userId, staleTime });

  const hasActiveFilters = createMemo(() => {
    const f = filters();
    return (
      f.term.trim().length > 0 ||
      f.mood !== null ||
      f.tagId !== null ||
      f.startKey.length > 0 ||
      f.endKey.length > 0
    );
  });

  const queryFilters = createMemo<EntryFilterArgs>(() => {
    const f = filters();
    return {
      term: f.term.trim() || undefined,
      mood: f.mood ?? undefined,
      tagId: f.tagId ?? undefined,
      startKey: f.startKey || undefined,
      endKey: f.endKey || undefined,
    };
  });

  const results = useFilterEntriesQuery({
    db,
    drizzle,
    userId,
    staleTime,
    filters: queryFilters,
  });

  const patch = (p: Partial<EntryFilterState>) => {
    const next = { ...filters(), ...p };
    setFilters(next);
    syncUrl(next);
  };
  const clear = () => {
    setFilters({ ...EMPTY_FILTERS });
    syncUrl({ ...EMPTY_FILTERS });
  };

  return (
    <PageContainer maxWidth="2xl" class="flex flex-col gap-4 pb-16">
      <header class="flex flex-col gap-3 pt-2">
        <h1 class="text-xl font-bold text-foreground">{t(msg`Search your journal`)}</h1>
        <EntryFilters
          state={filters}
          onChange={patch}
          onClear={clear}
          tags={() => tags.data ?? []}
          hasActiveFilters={hasActiveFilters}
        />
      </header>

      <Show
        when={hasActiveFilters()}
        fallback={
          <EmptyState
            icon={SearchIcon}
            illustration={<SearchIllustration />}
            title={t(msg`Search your journal`)}
            description={t(msg`Everything is searched locally on your device.`)}
          />
        }
      >
        <Show
          when={(results.data ?? []).length > 0}
          fallback={
            <Show when={!results.isLoading}>
              <EmptyState
                icon={SearchIcon}
                title={t(msg`No results found`)}
                description={t(msg`Everything is searched locally on your device.`)}
              />
            </Show>
          }
        >
          <p class="px-1 text-xs text-muted-foreground/60" data-testid="search-count">
            {t(msg`{count} entries found`, { count: (results.data ?? []).length })}
          </p>
          <section class="flex flex-col gap-3" data-testid="search-results">
            <For each={results.data ?? []}>
              {(entry) => <EntryCard entry={entry} showDate highlightTerm={filters().term} />}
            </For>
          </section>
        </Show>
      </Show>
    </PageContainer>
  );
}

export const searchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/search",
  validateSearch: (raw: Record<string, unknown>): SearchParams => {
    const moodNum = Number(raw.mood);
    return {
      mood: Number.isFinite(moodNum) && moodNum > 0 ? moodNum : undefined,
      tagId: typeof raw.tagId === "string" ? raw.tagId : undefined,
      term: typeof raw.term === "string" && raw.term.trim() !== "" ? raw.term : undefined,
      from: typeof raw.from === "string" && raw.from !== "" ? raw.from : undefined,
      to: typeof raw.to === "string" && raw.to !== "" ? raw.to : undefined,
    };
  },
  component: SearchPage,
});
